"use client";

import React, { useState, useEffect } from "react";
import { Input, Avatar, Badge, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import useDataMainStore from "../../stores/dataMainStore";
import LanguageSwitcher from "../LanguageSwitcher.tsx";
import useLanguageStore from "../../stores/languageStore.ts";

interface HeaderProps {
  onMenuClick: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useDataMainStore();
  const { t } = useLanguageStore();

  const userMenuItems: MenuProps["items"] = [
    {
      key: "settings",
      label: (
        <div className="flex items-center gap-2">
          <i className="fa-solid fa-gear text-black"></i>
          <span>{t("navigation.settings")}</span>
        </div>
      ),
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: (
        <div className="flex items-center gap-2 text-red-600">
          <i className="fa-solid fa-right-from-bracket"></i>
          <span>{t("auth.logout")}</span>
        </div>
      ),
      danger: true,
    },
  ];

  const handleUserMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key === "logout") {
      logout();
      localStorage.clear();
      navigate("/admin/login");
    }
  };

  // Get role display text with color
  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, { text: string; color: string }> = {
      admin: { text: "Administrator", color: "gold" },
      user: { text: "User", color: "blue" },
      moderator: { text: "Moderator", color: "purple" },
    };
    return roleMap[role] || { text: role, color: "default" };
  };

  const roleDisplay = user?.role ? getRoleDisplay(user.role) : { text: "User", color: "default" };

  // Domain Switcher logic
  const DOMAINS = {
    local: "http://localhost:8000/api",
    dev: "https://main.be.trove.id.vn/api",
    production: "https://bedev.trove.id.vn/api",
  };

  const STORAGE_KEY = "api-domain-preference";
  const [currentDomain, setCurrentDomain] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY);
      // Đảm bảo mặc định là production
      if (!saved || !["local", "dev", "production"].includes(saved)) {
        localStorage.setItem(STORAGE_KEY, "production");
        localStorage.setItem("api-base-url", DOMAINS.production);
        return "production";
      }
      return saved;
    }
    return "production";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setCurrentDomain(saved);
      }
    }
  }, []);

  const getDomainLabel = () => {
    const labels = {
      local: "Local",
      dev: "Dev",
      production: "Prod",
    };
    return labels[currentDomain as keyof typeof labels] || "Unknown";
  };

  const getDomainColor = () => {
    const colors = {
      local: "#fa8c16", // Orange
      dev: "#1890ff", // Blue
      production: "#52c41a", // Green
    };
    return colors[currentDomain as keyof typeof colors] || "#fa8c16";
  };

  const getDomainIcon = () => {
    const icons = {
      local: "fa-server",
      dev: "fa-code-branch",
      production: "fa-cloud",
    };
    return icons[currentDomain as keyof typeof icons] || "fa-server";
  };

  const getNextDomainLabel = () => {
    const domainOrder: Array<keyof typeof DOMAINS> = ["local", "dev", "production"];
    const currentIndex = domainOrder.indexOf(currentDomain as keyof typeof DOMAINS);
    const nextIndex = (currentIndex + 1) % domainOrder.length;
    const nextDomain = domainOrder[nextIndex];
    const labels = {
      local: "Local",
      dev: "Dev",
      production: "Production",
    };
    return labels[nextDomain] || "Unknown";
  };

  const handleDomainSwitch = () => {
    const domainOrder: Array<keyof typeof DOMAINS> = ["local", "dev", "production"];
    const currentIndex = domainOrder.indexOf(currentDomain as keyof typeof DOMAINS);
    const nextIndex = (currentIndex + 1) % domainOrder.length;
    const newDomain = domainOrder[nextIndex];

    setCurrentDomain(newDomain);
    localStorage.setItem(STORAGE_KEY, newDomain);
    localStorage.setItem("api-base-url", DOMAINS[newDomain]);

    // Reload to apply new domain
    window.location.reload();
  };

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={onMenuClick}
            className="p-2 hover:bg-yellow-50 rounded-xl transition-all duration-200 text-gray-600 hover:text-primary-600"
          >
            <i className="fa-solid fa-bars text-black text-xl"></i>
          </button>

          <Input
            placeholder="Search or type command..."
            prefix={
              <i className="fa-solid fa-magnifying-glass text-gray-400 text-lg"></i>
            }
            suffix={
              <kbd className="px-2 py-1 text-xs bg-gray-100 rounded-lg border border-gray-200 text-gray-500 font-medium">
                ⌘K
              </kbd>
            }
            className="hover:border-primary-400 focus:border-primary-500 transition-all w-full max-w-xl"
            style={{
              borderRadius: "1rem",
              backgroundColor: "#FAFAFA",
              border: "1px solid #f0f0f0"
            }}
          />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button className="p-2 hover:bg-yellow-50 rounded-xl transition-all duration-200 text-gray-600 hover:text-primary-600 relative">
            <i className="fa-solid fa-moon text-black text-xl"></i>
          </button>

          {/* Domain Switcher */}
          <Tooltip title={`API: ${getDomainLabel()} | Click to switch to ${getNextDomainLabel()}`}>
            <button
              onClick={handleDomainSwitch}
              className="p-2 hover:bg-yellow-50 rounded-xl transition-all duration-200 relative group"
              style={{
                backgroundColor: getDomainColor() + "15",
                border: `1px solid ${getDomainColor()}40`,
              }}
            >
              <div className="flex items-center gap-1.5">
                <i className={`fa-solid ${getDomainIcon()} text-sm`} style={{ color: getDomainColor() }}></i>
                <span className="text-xs font-semibold" style={{ color: getDomainColor() }}>
                  {getDomainLabel()}
                </span>
              </div>
            </button>
          </Tooltip>

          <LanguageSwitcher />

          <Badge count={3} size="small" color="#FFCB37" offset={[-2, 2]}>
            <button className="p-2 hover:bg-yellow-50 rounded-xl transition-all duration-200 text-gray-600 hover:text-primary-600">
              <i className="fa-solid fa-bell text-black text-xl"></i>
            </button>
          </Badge>

          <Dropdown
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex items-center gap-3 cursor-pointer hover:bg-yellow-50 px-3 py-2 rounded-2xl transition-all duration-200 border border-transparent hover:border-primary-200 hover:shadow-sm">
              <Avatar
                src={user?.avatar || undefined}
                size={42}
                className="border-2 border-primary-200 shadow-sm bg-gradient-to-br from-yellow-100 to-yellow-200"
                style={{
                  backgroundColor: user?.avatar ? undefined : "#FEF3C7"
                }}
              >
                {!user?.avatar && (
                  <span className="text-primary-600 font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase() || "U"}
                  </span>
                )}
              </Avatar>
              <div className="text-left hidden md:block">
                <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  {user?.name || "Admin User"}
                  {user?.role === "admin" && (
                    <i className="fa-solid fa-shield-halved text-primary-500 text-xs"></i>
                  )}
                </div>
                <div className="text-xs text-gray-500 font-medium">
                  {user?.email || "admin@example.com"}
                </div>
              </div>
              <i className="fa-solid fa-chevron-down text-gray-400 text-xs transition-transform group-hover:rotate-180"></i>
            </div>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
